import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CircularProgress,
	Divider,
	Skeleton,
	Stack,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { LockIcon } from "@phosphor-icons/react/dist/ssr/Lock";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { usePreparationOptions } from "@/hooks/api/use-campaign-preparation";
import { useCampaignRequest } from "@/hooks/api/use-campaign-requests";
import { useAuth } from "@/components/core/auth-provider";

import { BlockerChips, RequestNotFound, StateChip } from "../../components";
import { formatDateTime } from "../../constants";
import { humaniseEnum, requestTitle } from "../../humanise";
import { readinessOf } from "./readiness";
import { AgentsTab } from "./tab-agents";
import { LeadsTab } from "./tab-leads";
import { OverviewTab } from "./tab-overview";
import { ProcessingTab } from "./tab-processing";
import { ProvisionTab } from "./tab-provision";

/**
 * Preparation: the per-request workspace where the manual hour is spent, and the one button that
 * ends it.
 *
 * Four tabs, one job each. It was one column of eight stacked panels — a call-flow upload beside
 * an essay about a prompt generator that does not exist, an agents table, a cleaning report, a
 * bindings table, an approval record, a timeline and the provision button — and reading it end to
 * end told you almost nothing about which of those you were actually there to do. Nothing has been
 * removed except the explanations; what is left is sorted by the question being asked.
 *
 * Super-admin only. `POST /:request/provision` carries no `@AllowForRoles`, so it resolves to
 * super-admin and answers anyone else a 403; the gate here explains that rather than letting the
 * screen render a button nobody can press.
 */
export function RequestPreparation(): React.JSX.Element {
	const { user, isLoading: isAuthLoading } = useAuth();

	if (isAuthLoading) {
		return (
			<PageShell>
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<CircularProgress />
				</Box>
			</PageShell>
		);
	}

	if (!user?.isSuperAdmin) {
		return (
			<PageShell>
				<Alert severity="warning" icon={<LockIcon />}>
					<AlertTitle>Preparation is super-admin only</AlertTitle>
					<Typography variant="body2">
						Editing a settled request and provisioning it are both super-admin work. The client&rsquo;s own view of the
						same request is on <strong>Campaign requests</strong>.
					</Typography>
				</Alert>
			</PageShell>
		);
	}

	return <PreparationWorkspace />;
}

/** The page frame: back link, and whatever the screen has to say. */
function PageShell({ children }: { children: React.ReactNode }): React.JSX.Element {
	const navigate = useNavigate();

	return (
		<Box
			sx={{
				m: "var(--Content-margin)",
				maxWidth: "var(--Content-maxWidth)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={3}>
				<Box>
					<Button
						size="small"
						color="secondary"
						startIcon={<ArrowLeftIcon />}
						onClick={() => navigate("/platform/campaign-requests")}
					>
						Request Queue
					</Button>
				</Box>
				{children}
			</Stack>
		</Box>
	);
}

type TabValue = "processing" | "overview" | "agents" | "leads" | "provision";

const TABS: { value: TabValue; label: string }[] = [
	{ value: "processing", label: "Processing & Approval" },
	{ value: "overview", label: "Overview" },
	{ value: "agents", label: "Agents" },
	{ value: "leads", label: "Leads" },
	{ value: "provision", label: "Provision" },
];

function PreparationWorkspace(): React.JSX.Element {
	const { requestId } = useParams<{ requestId: string }>();
	const { data: preparationOptions } = usePreparationOptions(requestId ?? "");

	const { data: request, isLoading, isError, error } = useCampaignRequest(requestId);
	const [params, setParams] = useSearchParams();
	const tab = (TABS.some((entry) => entry.value === params.get("tab")) ? params.get("tab") : "processing") as TabValue;
	const setTab = (value: TabValue) =>
		setParams((previous) => {
			const next = new URLSearchParams(previous);
			next.set("tab", value);
			return next;
		});

	if (isLoading) {
		// The shape of the workspace rather than a spinner in the middle of nothing: the header, the
		// tab bar, the panel under it. A spinner says "wait"; this says what you are waiting for,
		// and the page does not jump when it arrives.
		return (
			<PageShell>
				<Stack spacing={3}>
					<Skeleton variant="rounded" height={72} />
					<Skeleton variant="rounded" height={420} />
				</Stack>
			</PageShell>
		);
	}

	if (isError || !request) {
		const status = (error as any)?.response?.status;

		/*
		 * The way back is the queue, not the client list — an operator who got here came from
		 * `/platform/campaign-requests` and has no workspace selected, so sending them to the
		 * tenant-scoped list would be a second dead end.
		 */
		return (
			<RequestNotFound
				backLabel="Back to the queue"
				backTo="/platform/campaign-requests"
				detail={status === 403 ? null : ((error as any)?.response?.data?.message ?? null)}
				variant={status === 403 ? "forbidden" : "missing"}
			/>
		);
	}

	const readiness = readinessOf(request);

	return (
		<PageShell>
			<Stack
				direction={{ sm: "row", xs: "column" }}
				spacing={2}
				sx={{ alignItems: { sm: "center", xs: "flex-start" } }}
			>
				<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
					{/* A request submitted without a name is stored titled after its objective, so the
					    raw column value would read `appointment_setting` in 24pt at the top of the
					    workspace. */}
					<Typography variant="h4">{requestTitle(request.title)}</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{[
							preparationOptions?.account.name,
							request.clientReference,
							request.confirmedAt
								? `confirmed ${formatDateTime(request.confirmedAt)}${
										request.confirmedByName
											? ` by ${request.confirmedByName}${
													request.confirmedByRole ? `, ${humaniseEnum(request.confirmedByRole)}` : ""
												}`
											: ""
									}`
								: "not confirmed yet",
						]
							.filter(Boolean)
							.join(" · ")}
					</Typography>
				</Box>

				<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
					<BlockerChips blockers={request.blockers} emptyContent={null} />
					<StateChip state={request.state} variant="admin" size="medium" />
				</Stack>
			</Stack>

			<Card>
				<Tabs onChange={(_, value: TabValue) => setTab(value)} sx={{ px: 3 }} value={tab} variant="scrollable">
					{TABS.map((entry) => (
						<Tab key={entry.value} label={entry.label} sx={{ minHeight: "auto" }} tabIndex={0} value={entry.value} />
					))}
				</Tabs>

				<Divider />

				<Box sx={{ p: 3 }}>
					{tab === "processing" ? <ProcessingTab request={request} /> : null}
					{tab === "overview" ? <OverviewTab request={request} readiness={readiness} /> : null}
					{tab === "agents" ? <AgentsTab request={request} /> : null}
					{tab === "leads" ? <LeadsTab request={request} /> : null}
					{tab === "provision" ? <ProvisionTab request={request} readiness={readiness} /> : null}
				</Box>
			</Card>
		</PageShell>
	);
}
